'use client';

import { useEffect, useRef, useState } from 'react';
import { codeLines, copyText, type CodeLang } from './data/highlight';

/**
 * Dark (ink) code pane with mono label bar and optional COPY → COPIED
 * (1.4s) button — the one code surface every docs page uses. Highlighting
 * is the kit-convention regex tokenizer; colors are the RiiKit CODE map.
 */
export default function CodePane({
  src,
  lang,
  label,
  copy = false,
  size = 'md',
  shadow = false,
}: {
  src: string;
  lang: CodeLang;
  label: string;
  copy?: boolean;
  /** md = quickstart panes (12px), sm = reference panes (11.5px). */
  size?: 'sm' | 'md';
  shadow?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onCopy = () => {
    // Clipboard API with silent fallback — the COPIED state still confirms.
    navigator.clipboard?.writeText(copyText(src)).catch(() => {});
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1400);
  };

  return (
    <div
      className={`overflow-hidden rounded-xl bg-ink ${shadow ? 'shadow-[0_14px_34px_-22px_rgba(16,16,40,.35)]' : ''}`}
    >
      <div className="flex items-center gap-2.5 border-b border-white/[0.09] px-3.5 py-2">
        <span className="font-jetbrains text-[9.5px] tracking-[0.12em] text-white/55">{label}</span>
        {copy && (
          <button
            type="button"
            onClick={onCopy}
            className="ml-auto cursor-pointer font-jetbrains text-[9.5px] tracking-[0.08em] text-white/50 transition-colors hover:text-white"
          >
            {copied ? 'COPIED' : 'COPY'}
          </button>
        )}
      </div>
      <div
        className={`overflow-x-auto px-4 py-[13px] font-jetbrains leading-[1.7] ${size === 'md' ? 'text-[12px]' : 'text-[11.5px]'}`}
      >
        {codeLines(src, lang).map((line, i) => (
          <div key={i} className={`whitespace-pre ${size === 'md' ? 'min-h-[20px]' : 'min-h-[19px]'}`}>
            {line.map((seg, j) => (
              <span key={j} style={{ color: seg.c }}>
                {seg.t}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
